/**
 * QuoteAutomatedPreparationReviewPanel — W15-E02-006 Ready to Send review UI.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  approveAutomatedPreparation,
  adoptAutomatedPreparationEstimate,
  adoptAutomatedPreparationPricing,
  fetchAutomatedPreparationReview,
  matchCatalogueLineForPreparation,
  repriceAutomatedPreparation,
} from "../services/automatedPreparationReviewService.js";
import { isAutomatedPreparationApprovedForSend } from "../utils/quotePdfReadiness.js";
import {
  buildQuoteReadinessChecklist,
  summarizeReadinessChecklist,
} from "../utils/quoteReadinessChecklist.js";
import QuoteReadinessChecklist from "./QuoteReadinessChecklist.jsx";
import QuotePreparationDrawer from "./QuotePreparationDrawer.jsx";
import InventoryCatalogueMatchDrawer from "./InventoryCatalogueMatchDrawer.jsx";

const Panel = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
}));

const QuoteAutomatedPreparationReviewPanel = ({
  quote,
  onApproved = null,
  onRevisionCreated = null,
  onReviewChange = null,
}) => {
  const quoteId = quote?.id;
  const hasAutomatedPrep = Boolean(quote?.automatedPreparation);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [adoptingPricing, setAdoptingPricing] = useState(false);
  const [repricing, setRepricing] = useState(false);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [preparationDrawerOpen, setPreparationDrawerOpen] = useState(false);
  const [catalogueDrawerItem, setCatalogueDrawerItem] = useState(null);
  const [matchingCatalogue, setMatchingCatalogue] = useState(false);
  const [scheduledInputVersion, setScheduledInputVersion] = useState(null);
  const [waitingForRevision, setWaitingForRevision] = useState(false);

  const loadReview = useCallback(async ({ silent = false } = {}) => {
    if (!quoteId || !hasAutomatedPrep) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    const result = await fetchAutomatedPreparationReview(quoteId);
    if (!silent) {
      setLoading(false);
    }
    if (!result.success) {
      if (!silent) {
        setError(result.error || "Failed to load review");
        setReview(null);
      }
      return;
    }
    setReview(result.data);
  }, [quoteId, hasAutomatedPrep]);

  useEffect(() => {
    if (typeof onReviewChange === "function") {
      onReviewChange(review);
    }
  }, [review, onReviewChange]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  useEffect(() => {
    // Only poll while something is actually in-flight. Do NOT poll forever just
    // because catalogue exists and OS AI is expected — failed/missing secondary
    // without status=pending was causing Preparation & send to reload every 2s.
    const secondaryStatus = review?.secondaryEnrichmentStatus?.status;
    const secondaryPending =
      secondaryStatus === "pending" &&
      Boolean(review?.enrichmentReports?.catalogue) &&
      !review?.enrichmentReports?.ai &&
      !review?.enrichmentReports?.n8n;
    const waiting =
      repricing ||
      waitingForRevision ||
      review?.repriceStatus === "pending" ||
      secondaryPending ||
      (scheduledInputVersion != null &&
        Number(review?.inputVersion) < Number(scheduledInputVersion));
    if (!waiting || !quoteId) return undefined;

    const timer = setInterval(() => {
      loadReview({ silent: true });
    }, 2000);

    return () => clearInterval(timer);
  }, [
    repricing,
    waitingForRevision,
    review?.repriceStatus,
    review?.inputVersion,
    review?.enrichmentReports,
    review?.secondaryEnrichmentStatus?.status,
    scheduledInputVersion,
    quoteId,
    loadReview,
  ]);

  useEffect(() => {
    if (review?.supersededByQuoteId && waitingForRevision) {
      setRepricing(false);
      setWaitingForRevision(false);
      if (typeof onRevisionCreated === "function") {
        onRevisionCreated(review.supersededByQuoteId);
      } else if (typeof onApproved === "function") {
        onApproved(review);
      }
      return;
    }
    if (
      waitingForRevision &&
      ["accepted", "pending_payment", "deposit_paid", "deposit_verified", "ordered"].includes(
        review?.status
      )
    ) {
      setRepricing(false);
      setWaitingForRevision(false);
      setError(
        "This quote was funded before the revision could replace it. Check the lead for a new draft."
      );
      return;
    }
    if (scheduledInputVersion == null) return;
    if (Number(review?.inputVersion) >= Number(scheduledInputVersion)) {
      setRepricing(false);
      setScheduledInputVersion(null);
      if (typeof onApproved === "function") {
        onApproved(review);
      }
    }
  }, [
    review,
    scheduledInputVersion,
    waitingForRevision,
    onApproved,
    onRevisionCreated,
  ]);

  const handleReprice = async () => {
    if (!quoteId) return;
    setRepricing(true);
    setError(null);
    const result = await repriceAutomatedPreparation(quoteId);
    if (!result.success) {
      setRepricing(false);
      setError(result.error || "Failed to re-run pricing");
      return;
    }
    if (result.data?.mode === "revise") {
      setWaitingForRevision(true);
    } else {
      const nextVersion = Number(result.data?.inputVersion);
      if (Number.isFinite(nextVersion)) {
        setScheduledInputVersion(nextVersion);
      }
    }
    await loadReview();
  };

  const handleApprove = async () => {
    if (!quoteId) return;
    setApproving(true);
    setError(null);
    const result = await approveAutomatedPreparation(quoteId, {});
    setApproving(false);
    if (!result.success) {
      setError(result.error || "Approval failed");
      return;
    }
    setReview(result.data);
    if (typeof onApproved === "function") {
      onApproved(result.data);
    }
  };

  const handleAdoptPricing = async () => {
    if (!quoteId) return;
    setAdoptingPricing(true);
    setError(null);
    const result = await adoptAutomatedPreparationPricing(quoteId);
    setAdoptingPricing(false);
    if (!result.success) {
      setError(result.error || "Adopt pricing failed");
      return;
    }
    setReview(result.data);
    if (typeof onApproved === "function") {
      onApproved(result.data);
    }
  };

  const handleAdopt = async (source) => {
    if (!quoteId) return;
    setAdopting(true);
    setError(null);
    const result = await adoptAutomatedPreparationEstimate(quoteId, { source });
    setAdopting(false);
    if (!result.success) {
      setError(result.error || "Adopt failed");
      return;
    }
    setReview(result.data);
    if (typeof onApproved === "function") {
      onApproved(result.data);
    }
  };

  const handleMatchCatalogue = async ({ lineKey, catalogueSkuId }) => {
    if (!quoteId || !lineKey || !catalogueSkuId) return;
    setMatchingCatalogue(true);
    setError(null);
    const result = await matchCatalogueLineForPreparation(quoteId, lineKey, {
      catalogueSkuId,
    });
    setMatchingCatalogue(false);
    if (!result.success) {
      setError(result.error || "Catalogue match failed");
      return;
    }
    setReview(result.data);
    const updatedItems = result.data?.inventory?.items || [];
    const updatedLine =
      updatedItems.find((row) => row.sourceItemId === lineKey) ||
      updatedItems.find((row) => row.key === lineKey) ||
      updatedItems.find((row) => row.catalogueSkuId === catalogueSkuId);
    if (updatedLine) {
      setCatalogueDrawerItem(updatedLine);
    }
    if (typeof onApproved === "function") {
      onApproved(result.data);
    }
  };

  if (!hasAutomatedPrep) {
    return null;
  }

  const approved =
    Boolean(review?.readyToSend) ||
    isAutomatedPreparationApprovedForSend(quote);
  const checklist = buildQuoteReadinessChecklist(quote, review);
  const readinessSummary = summarizeReadinessChecklist(checklist);

  const getCatalogueCellSummary = (item) => {
    const match = item?.catalogueMatch;
    if (match?.matchStatus === "unique" && match?.matchedSku?.displayName) {
      return match.matchedSku.displayName;
    }
    const count = match?.suggestions?.length || 0;
    if (count > 0) {
      return `${count} suggestion${count === 1 ? "" : "s"}`;
    }
    return null;
  };

  return (
    <>
      <Panel elevation={0} data-testid="quote-automated-preparation-review">
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={1}
            mb={1}
          >
            <Box flex={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Preparation & send
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Inventory, enrichment, PDFs, and approval
              </Typography>
            </Box>
            <Chip
              size="small"
              label={readinessSummary.headline}
              color={
                readinessSummary.tone === "success"
                  ? "success"
                  : readinessSummary.tone === "warning"
                    ? "warning"
                    : "default"
              }
            />
          </Stack>

          {loading && (
            <Box display="flex" justifyContent="center" py={1.5}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && !preparationDrawerOpen && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          {!loading && (
            <>
              <QuoteReadinessChecklist quote={quote} review={review} dense />
              <Stack direction="row" gap={1} flexWrap="wrap" mt={1.5}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setPreparationDrawerOpen(true)}
                  data-testid="open-preparation-drawer"
                >
                  Open preparation
                </Button>
                {!approved && review?.preparationReady && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={approving}
                    onClick={handleApprove}
                  >
                    {approving ? "Approving…" : "Approve for send"}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </CardContent>
      </Panel>

      <QuotePreparationDrawer
        open={preparationDrawerOpen}
        onClose={() => setPreparationDrawerOpen(false)}
        quote={quote}
        review={review}
        approved={approved}
        loading={loading}
        error={error}
        approving={approving}
        adopting={adopting}
        adoptingPricing={adoptingPricing}
        repricing={repricing}
        waitingForRevision={waitingForRevision}
        onApprove={handleApprove}
        onReprice={handleReprice}
        onAdopt={handleAdopt}
        onAdoptPricing={handleAdoptPricing}
        onOpenCatalogueMatch={(item) => setCatalogueDrawerItem(item)}
        getCatalogueCellSummary={getCatalogueCellSummary}
      />

      <InventoryCatalogueMatchDrawer
        open={Boolean(catalogueDrawerItem)}
        item={catalogueDrawerItem}
        quoteId={quoteId}
        matching={matchingCatalogue}
        onClose={() => setCatalogueDrawerItem(null)}
        onMatchCatalogue={handleMatchCatalogue}
      />
    </>
  );
};

export default QuoteAutomatedPreparationReviewPanel;
